import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  Box, Typography, Card, CardContent, Grid, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, CircularProgress, Alert, TablePagination
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import DraftsIcon from '@mui/icons-material/Drafts'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { dcApi } from '../services/dc.api'
import { DCStoreTransfer } from '../types/dc.types'

const statusColors: Record<string, 'default' | 'primary' | 'info' | 'success'> = {
  draft: 'default',
  confirmed: 'primary',
  exported: 'info',
  completed: 'success'
}

export default function DCTransfersPage() {
  const navigate = useNavigate()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(20)

  const { data, isLoading, error } = useQuery({
    queryKey: ['transfers', page, rowsPerPage],
    queryFn: () => dcApi.getTransfers({ page: page + 1, limit: rowsPerPage })
  })

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return <Alert severity="error">Failed to load transfers: {(error as Error).message}</Alert>
  }

  const transfers = data?.transfers || []
  const totalCount = data?.pagination?.total || 0

  const draftCount = transfers.filter((t: DCStoreTransfer) => t.status === 'draft').length
  const completedCount = transfers.filter((t: DCStoreTransfer) => t.status === 'completed').length

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Store Transfers</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/transfers/create')}
        >
          New Transfer
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <LocalShippingIcon color="primary" fontSize="large" />
              <Box>
                <Typography variant="h6">{totalCount}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Transfers
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <DraftsIcon color="action" fontSize="large" />
              <Box>
                <Typography variant="h6">{draftCount}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Draft
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CheckCircleIcon color="success" fontSize="large" />
              <Box>
                <Typography variant="h6">{completedCount}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Completed
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Transfers Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Transfer #</TableCell>
              <TableCell>Store</TableCell>
              <TableCell>Date</TableCell>
              <TableCell align="right">Items</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created By</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transfers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="text.secondary" sx={{ py: 3 }}>
                    No transfers yet. Click "New Transfer" to create one.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              transfers.map((transfer: DCStoreTransfer) => (
                <TableRow
                  key={transfer.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/transfers/${transfer.id}`)}
                >
                  <TableCell>
                    <Typography fontWeight={600}>{transfer.transfer_number}</Typography>
                  </TableCell>
                  <TableCell>{transfer.store_name}</TableCell>
                  <TableCell>
                    {transfer.transfer_date
                      ? new Date(transfer.transfer_date).toLocaleDateString('en-IN')
                      : transfer.created_at
                        ? new Date(transfer.created_at).toLocaleDateString('en-IN')
                        : '-'}
                  </TableCell>
                  <TableCell align="right">{transfer.total_items}</TableCell>
                  <TableCell align="right">
                    Rs. {(transfer.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={transfer.status}
                      size="small"
                      color={statusColors[transfer.status] || 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {transfer.created_by || '-'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10))
            setPage(0)
          }}
          rowsPerPageOptions={[10, 20, 50]}
        />
      </TableContainer>
    </Box>
  )
}
